import { useState, useEffect } from "react";
import { rootUrl } from "../../helpers";
import { useAuth } from "../../context/auth-context";
import { 
  FaSearch, 
  FaCalendarAlt, 
  FaArrowLeft, 
  FaArrowRight,
  FaMoneyBillWave,
  FaFileInvoice,
  FaUsers,
  FaArrowUp,
  FaArrowDown,
  FaUndo,
  FaEye,
  FaChartLine,
  FaClipboardCheck
} from "react-icons/fa";

const ROWS_PER_PAGE = 10;

const paginate = (data, page, rowsPerPage) => {
  const start = page * rowsPerPage;
  return data.slice(start, start + rowsPerPage);
};

const AuditPage = () => {
  const auth = useAuth();
  const [auditData, setAuditData] = useState([]);
  const [cashbookData, setCashbookData] = useState([]);
  const [users, setUsers] = useState([]);
  const [pvAuditData, setPvAuditData] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [tab, setTab] = useState("transactions");
  const [selectedUser, setSelectedUser] = useState(null);
  // Pagination states
  const [txPage, setTxPage] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [userTxPage, setUserTxPage] = useState(0);
  const [userAuditPage, setUserAuditPage] = useState(0);

  const fetchData = async () => {
    if (!auth?.user?.id) return;
    if (!startDate || !endDate) return;

    setIsLoading(true);
    try {
      const payload = `${auth.user.id}~${startDate}~${endDate}`;
      const response = await fetch(rootUrl(`/auditReport/${encodeURIComponent(payload)}`));
      const data = await response.json();

      const allUsers = data?.data?.users || [];
      const allAudit = data?.data?.audit || [];
      const allCashbook = data?.data?.cashbook || [];
      const allPvAudit = data?.data?.pvAudit || [];

      setAuditData(allAudit);
      setCashbookData(allCashbook);
      setUsers(allUsers);
      setPvAuditData(allPvAudit);
      setHasFetched(true);
      setSelectedUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserId = (user) => String(user?.u_id ?? user?.id ?? "");
  const getUserById = (id) =>
    users.find((user) => getUserId(user) === String(id))?.firstname || "Unknown";
  const parseJsonField = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  };
  const labelForField = (field) => {
    const labels = {
      date: "Date",
      description: "Description",
      cheque: "Cheque",
      proj_id: "Project",
      coa_id: "COA",
      sof: "Source of Fund",
      bank: "Bank",
      status: "Status",
      amount: "Amount",
      voucher: "Voucher",
      type: "Type",
    };
    return labels[field] || field;
  };
  const actionTone = (actionType) => {
    if (actionType === "cashbook_edit") return "bg-blue-100 text-blue-700";
    if (actionType === "cashbook_delete") return "bg-red-100 text-red-700";
    if (actionType === "cashbook_status_change") return "bg-amber-100 text-amber-700";
    return "bg-gray-100 text-gray-700";
  };

  // Filters for Transactions
  const filteredCashbook = cashbookData.filter(
    (entry) =>
      (!search || getUserById(entry.updatedBy).toLowerCase().includes(search.toLowerCase()))
  );

  // Filters for Audit Log
  const filteredAudit = auditData.filter(
    (entry) =>
      (!search || getUserById(entry.uid).toLowerCase().includes(search.toLowerCase()))
  );

  const filteredPvAudit = pvAuditData.filter((entry) => {
    const actor = `${entry.firstname || ""} ${entry.lastname || ""}`.toLowerCase();
    return (
      !search ||
      actor.includes(search.toLowerCase()) ||
      String(entry.voucher || "").toLowerCase().includes(search.toLowerCase())
    );
  });

  // By User: list of users with counts
  const userActions = users.map((user) => {
    const userId = getUserId(user);
    const userTransactions = cashbookData.filter((entry) => String(entry.updatedBy) === userId);
    const userAudits = auditData.filter((entry) => String(entry.uid) === userId);
    return {
      ...user,
      transactionCount: userTransactions.length,
      auditCount: userAudits.length,
      transactions: userTransactions,
      audits: userAudits,
    };
  });

  // Transaction totals
  const totalIncome = filteredCashbook.filter(e => e.type === 'income').reduce((sum, e) => sum + Number(e.amount), 0);
  const totalExpense = filteredCashbook.filter(e => e.type === 'expense').reduce((sum, e) => sum + Number(e.amount), 0);
  const net = totalIncome - totalExpense;

  // Paginated data
  const pagedTx = paginate(filteredCashbook, txPage, ROWS_PER_PAGE);
  const pagedAudit = paginate(filteredAudit, auditPage, ROWS_PER_PAGE);
  const pagedUsers = paginate(userActions, userPage, ROWS_PER_PAGE);
  const pagedUserTx = selectedUser ? paginate(selectedUser.transactions, userTxPage, ROWS_PER_PAGE) : [];
  const pagedUserAudit = selectedUser ? paginate(selectedUser.audits, userAuditPage, ROWS_PER_PAGE) : [];

  // Reset page on tab/filter change
  useEffect(() => { setTxPage(0); }, [search, tab, auditData, cashbookData]);
  useEffect(() => { setAuditPage(0); }, [search, tab, auditData, cashbookData]);
  useEffect(() => { setUserPage(0); }, [tab]);
  useEffect(() => { setUserTxPage(0); setUserAuditPage(0); }, [selectedUser]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3">
            <FaChartLine className="text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Audit</h1>
            <p className="text-blue-100 text-sm mt-1">
              Review cash transactions, reversals, and activity by user.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
      {/* Shared Filters */}
      <div className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="relative">
            <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by username"
              className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={fetchData}
            disabled={!startDate || !endDate || isLoading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-950 text-white hover:bg-blue-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? "Fetching..." : "Fetch Transactions"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => { setTab('transactions'); setSelectedUser(null); }}
          className={`px-4 py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 ${
            tab === 'transactions'
              ? 'bg-blue-950 text-white shadow-md transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
          }`}
        >
          <FaMoneyBillWave className={tab === 'transactions' ? 'text-white' : 'text-gray-600'} />
          Transactions
        </button>
        <button
          onClick={() => { setTab('audit'); setSelectedUser(null); }}
          className={`px-4 py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 ${
            tab === 'audit'
              ? 'bg-blue-950 text-white shadow-md transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
          }`}
        >
          <FaUndo className={tab === 'audit' ? 'text-white' : 'text-gray-600'} />
          Audit Log
        </button>
        <button
          onClick={() => setTab('users')}
          className={`px-4 py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 ${
            tab === 'users'
              ? 'bg-blue-950 text-white shadow-md transform scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
          }`}
        >
          <FaUsers className={tab === 'users' ? 'text-white' : 'text-gray-600'} />
          By User
        </button>
      </div>
      {!hasFetched && !isLoading && (
        <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-4 py-3 text-sm">
          Select start date and end date, then click <strong>Fetch Transactions</strong>.
        </div>
      )}

      {isLoading && (
        <div className="mb-6 flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <div className="w-4 h-4 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-700">Loading transactions for selected period...</span>
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'transactions' && (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-4">
            <FaFileInvoice className="text-blue-950 text-xl" />
            <h3 className="text-lg md:text-xl font-semibold text-gray-800">Cash Transactions</h3>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-950 to-blue-900">
                    <th className="px-4 py-3 text-left font-semibold text-white">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Description</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Performed By</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTx.map((entry) => (
                    <tr key={entry.id} className="border-t border-gray-100 hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700">{entry.date}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          entry.type === 'income' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {entry.type === 'income' ? (
                            <FaArrowUp className="text-xs" />
                          ) : (
                            <FaArrowDown className="text-xs" />
                          )}
                          {entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{Number(entry.amount).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600">{entry.description}</td>
                      <td className="px-4 py-3 text-gray-700">{getUserById(entry.updatedBy)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-green-50 font-semibold border-t-2 border-green-200">
                    <td colSpan={2} className="px-4 py-3 text-green-800">Total Income</td>
                    <td className="px-4 py-3 text-green-700 text-lg">{totalIncome.toLocaleString()}</td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr className="bg-red-50 font-semibold">
                    <td colSpan={2} className="px-4 py-3 text-red-800">Total Expense</td>
                    <td className="px-4 py-3 text-red-700 text-lg">{totalExpense.toLocaleString()}</td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr className={`font-bold border-t-2 ${net >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                    <td colSpan={2} className={`px-4 py-3 ${net >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>Net Balance</td>
                    <td className={`px-4 py-3 text-lg ${net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                      {net.toLocaleString()}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 text-xs md:text-sm">
            <button
              onClick={() => setTxPage(p => Math.max(0, p - 1))}
              disabled={txPage === 0}
              className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
            >
              <FaArrowLeft className="text-xs" />
              Previous
            </button>
            <span className="text-gray-600 font-medium">
              Page {txPage + 1} of {Math.ceil(filteredCashbook.length / ROWS_PER_PAGE)}
            </span>
            <button
              onClick={() => setTxPage(p => p + 1)}
              disabled={(txPage + 1) * ROWS_PER_PAGE >= filteredCashbook.length}
              className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
            >
              Next
              <FaArrowRight className="text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {tab === 'audit' && (
        <div className="mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-gray-200 bg-blue-50 p-3">
              <p className="text-xs font-semibold text-blue-900 uppercase">PV Audits</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{filteredPvAudit.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-green-50 p-3">
              <p className="text-xs font-semibold text-green-900 uppercase">Unique Auditors</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {new Set(filteredPvAudit.map((a) => String(a.uid))).size}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900 uppercase">Audited Amount</p>
              <p className="text-2xl font-bold text-amber-900 mt-1">
                {filteredPvAudit.reduce((sum, a) => sum + Number(a.amount || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm mb-5">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <FaClipboardCheck className="text-red-600" />
              <h4 className="font-semibold text-gray-800">Payment voucher audit summary</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-red-700 to-red-600">
                    <th className="px-4 py-3 text-left font-semibold text-white">Audit Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Voucher</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Voucher Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Auditor</th>
                    <th className="px-4 py-3 text-left font-semibold text-white">Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPvAudit.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 text-sm text-gray-500">
                        No PV audits found for the selected period.
                      </td>
                    </tr>
                  )}
                  {filteredPvAudit.slice(0, 100).map((entry) => (
                    <tr key={entry.id} className="border-t border-gray-100 hover:bg-red-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700">{String(entry.created_at || "").substring(0, 19)}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{entry.voucher || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{String(entry.voucher_date || "").substring(0, 10)}</td>
                      <td className="px-4 py-3 text-gray-700">{Number(entry.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-700">{`${entry.firstname || ""} ${entry.lastname || ""}`.trim() || "Unknown"}</td>
                      <td className="px-4 py-3 text-gray-600">{entry.comment || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <FaUndo className="text-red-600 text-xl" />
            <h3 className="text-lg md:text-xl font-semibold text-gray-800">Audit Trail</h3>
          </div>
          <div className="space-y-3">
            {pagedAudit.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
                No audit entries found for the selected period.
              </div>
            )}
            {pagedAudit.map((entry) => {
              const meta = parseJsonField(entry.meta) || {};
              const changedFields = meta.changed_fields || {};
              const beforeData = parseJsonField(entry.before_data) || {};
              const afterData = parseJsonField(entry.after_data) || {};
              const hasStructuredChanges = Object.keys(changedFields).length > 0;
              return (
                <div key={entry.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${actionTone(entry.action_type)}`}>
                        <FaUndo className="text-xs" />
                        {entry.action_type ? entry.action_type.replaceAll("_", " ") : "audit action"}
                      </span>
                      <span className="text-xs text-gray-500">Transaction #{entry.entity_id || entry.cid}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.date}</span>
                  </div>

                  <div className="text-sm text-gray-800 mb-2">{entry.text || "No description provided."}</div>
                  <div className="text-xs text-gray-500 mb-3">By: {getUserById(entry.uid)}</div>

                  {hasStructuredChanges && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                      <p className="text-xs font-semibold text-blue-900 mb-2">Changed Fields</p>
                      <div className="space-y-1.5">
                        {Object.entries(changedFields).map(([field, values]) => (
                          <div key={field} className="text-xs text-gray-700">
                            <span className="font-medium">{labelForField(field)}: </span>
                            <span className="text-gray-500 line-through mr-1">{values?.before ?? "-"}</span>
                            <span className="text-blue-700 font-medium">{values?.after ?? "-"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasStructuredChanges && (Object.keys(beforeData).length > 0 || Object.keys(afterData).length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Before</p>
                        <pre className="text-[11px] text-gray-600 whitespace-pre-wrap break-all">{JSON.stringify(beforeData, null, 2)}</pre>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                        <p className="text-xs font-semibold text-gray-700 mb-1">After</p>
                        <pre className="text-[11px] text-gray-600 whitespace-pre-wrap break-all">{JSON.stringify(afterData, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-4 text-xs md:text-sm">
            <button
              onClick={() => setAuditPage(p => Math.max(0, p - 1))}
              disabled={auditPage === 0}
              className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
            >
              <FaArrowLeft className="text-xs" />
              Previous
            </button>
            <span className="text-gray-600 font-medium">
              Page {auditPage + 1} of {Math.ceil(filteredAudit.length / ROWS_PER_PAGE)}
            </span>
            <button
              onClick={() => setAuditPage(p => p + 1)}
              disabled={(auditPage + 1) * ROWS_PER_PAGE >= filteredAudit.length}
              className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
            >
              Next
              <FaArrowRight className="text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* By User Tab */}
      {tab === 'users' && (
        <div className="mt-2">
          {!selectedUser ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FaUsers className="text-blue-950 text-xl" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Users</h3>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-600 to-purple-700">
                        <th className="px-4 py-3 text-left font-semibold text-white">User</th>
                        <th className="px-4 py-3 text-left font-semibold text-white">Transactions</th>
                        <th className="px-4 py-3 text-left font-semibold text-white">Reversals</th>
                        <th className="px-4 py-3 text-left font-semibold text-white">Drill Down</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedUsers.map((user) => (
                        <tr key={user.id} className="border-t border-gray-100 hover:bg-purple-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800">{user.firstname}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              {user.transactionCount}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              {user.auditCount}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              className="bg-blue-950 text-white px-4 py-1.5 rounded-lg text-xs md:text-sm hover:bg-blue-900 transition-colors flex items-center gap-2 font-medium shadow-sm"
                              onClick={() => setSelectedUser(user)}
                            >
                              <FaEye className="text-xs" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 text-xs md:text-sm">
                <button
                  onClick={() => setUserPage(p => Math.max(0, p - 1))}
                  disabled={userPage === 0}
                  className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                >
                  <FaArrowLeft className="text-xs" />
                  Previous
                </button>
                <span className="text-gray-600 font-medium">
                  Page {userPage + 1} of {Math.ceil(userActions.length / ROWS_PER_PAGE)}
                </span>
                <button
                  onClick={() => setUserPage(p => p + 1)}
                  disabled={(userPage + 1) * ROWS_PER_PAGE >= userActions.length}
                  className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                >
                  Next
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                className="mb-4 bg-gray-100 px-4 py-2 rounded-lg text-xs md:text-sm hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                onClick={() => setSelectedUser(null)}
              >
                <FaArrowLeft className="text-xs" />
                Back to Users
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FaUsers className="text-purple-700 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                    User: {selectedUser.firstname}
                  </h3>
                  <p className="text-sm text-gray-500">View detailed activity for this user</p>
                </div>
              </div>

              {/* User transactions */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <FaFileInvoice className="text-blue-600" />
                  <h4 className="font-semibold text-gray-800">Transactions</h4>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm mb-2">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-950 to-blue-900">
                          <th className="px-4 py-3 text-left font-semibold text-white">Date</th>
                          <th className="px-4 py-3 text-left font-semibold text-white">Type</th>
                          <th className="px-4 py-3 text-left font-semibold text-white">Amount</th>
                          <th className="px-4 py-3 text-left font-semibold text-white">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedUserTx.map((entry) => (
                          <tr key={entry.id} className="border-t border-gray-100 hover:bg-blue-50 transition-colors">
                            <td className="px-4 py-3 text-gray-700">{entry.date}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                entry.type === 'income' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {entry.type === 'income' ? (
                                  <FaArrowUp className="text-xs" />
                                ) : (
                                  <FaArrowDown className="text-xs" />
                                )}
                                {entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">{Number(entry.amount).toLocaleString()}</td>
                            <td className="px-4 py-3 text-gray-600">{entry.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs md:text-sm">
                  <button
                    onClick={() => setUserTxPage(p => Math.max(0, p - 1))}
                    disabled={userTxPage === 0}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                  >
                    <FaArrowLeft className="text-xs" />
                    Previous
                  </button>
                  <span className="text-gray-600 font-medium">
                    Page {userTxPage + 1} of {Math.ceil(selectedUser.transactions.length / ROWS_PER_PAGE)}
                  </span>
                  <button
                    onClick={() => setUserTxPage(p => p + 1)}
                    disabled={(userTxPage + 1) * ROWS_PER_PAGE >= selectedUser.transactions.length}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                  >
                    Next
                    <FaArrowRight className="text-xs" />
                  </button>
                </div>
              </div>

              {/* User audit log */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FaUndo className="text-red-600" />
                  <h4 className="font-semibold text-gray-800">Audit Trail (User)</h4>
                </div>
                <div className="space-y-3">
                  {pagedUserAudit.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
                      No user audit entries found for this period.
                    </div>
                  )}
                  {pagedUserAudit.map((entry) => {
                    const meta = parseJsonField(entry.meta) || {};
                    const changedFields = meta.changed_fields || {};
                    const beforeData = parseJsonField(entry.before_data) || {};
                    const afterData = parseJsonField(entry.after_data) || {};
                    const hasStructuredChanges = Object.keys(changedFields).length > 0;
                    return (
                      <div key={entry.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${actionTone(entry.action_type)}`}>
                              <FaUndo className="text-xs" />
                              {entry.action_type ? entry.action_type.replaceAll("_", " ") : "audit action"}
                            </span>
                            <span className="text-xs text-gray-500">Transaction #{entry.entity_id || entry.cid}</span>
                          </div>
                          <span className="text-xs text-gray-500">{entry.date}</span>
                        </div>

                        <div className="text-sm text-gray-800 mb-2">{entry.text || "No description provided."}</div>

                        {hasStructuredChanges && (
                          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                            <p className="text-xs font-semibold text-blue-900 mb-2">Changed Fields</p>
                            <div className="space-y-1.5">
                              {Object.entries(changedFields).map(([field, values]) => (
                                <div key={field} className="text-xs text-gray-700">
                                  <span className="font-medium">{labelForField(field)}: </span>
                                  <span className="text-gray-500 line-through mr-1">{values?.before ?? "-"}</span>
                                  <span className="text-blue-700 font-medium">{values?.after ?? "-"}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!hasStructuredChanges && (Object.keys(beforeData).length > 0 || Object.keys(afterData).length > 0) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                              <p className="text-xs font-semibold text-gray-700 mb-1">Before</p>
                              <pre className="text-[11px] text-gray-600 whitespace-pre-wrap break-all">{JSON.stringify(beforeData, null, 2)}</pre>
                            </div>
                            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                              <p className="text-xs font-semibold text-gray-700 mb-1">After</p>
                              <pre className="text-[11px] text-gray-600 whitespace-pre-wrap break-all">{JSON.stringify(afterData, null, 2)}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-3 text-xs md:text-sm">
                  <button
                    onClick={() => setUserAuditPage(p => Math.max(0, p - 1))}
                    disabled={userAuditPage === 0}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                  >
                    <FaArrowLeft className="text-xs" />
                    Previous
                  </button>
                  <span className="text-gray-600 font-medium">
                    Page {userAuditPage + 1} of {Math.ceil(selectedUser.audits.length / ROWS_PER_PAGE)}
                  </span>
                  <button
                    onClick={() => setUserAuditPage(p => p + 1)}
                    disabled={(userAuditPage + 1) * ROWS_PER_PAGE >= selectedUser.audits.length}
                    className="px-4 py-2 bg-gray-100 rounded-lg disabled:opacity-50 hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                  >
                    Next
                    <FaArrowRight className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default AuditPage;
